/**
 * ServiceOrderModal
 *
 * Full Zoho Books "New Sales Order" form — shown after service selection in
 * BookingForm. Mirrors the Zoho Books mobile UI with all real API fields.
 *
 * On confirm the draft is passed back to BookingForm via onConfirm().
 * The actual Zoho SO is created when the booking is successfully saved.
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import {
  IonModal,
  IonCard,
  IonCardContent,
  IonButton,
  IonContent,
  IonItem,
  IonLabel,
  IonInput,
  IonTextarea,
  IonIcon,
  IonList,
  IonRadioGroup,
  IonRadio,
  IonGrid,
  IonButtons,
} from '@ionic/react';
import {
  closeOutline,
  addCircleOutline,
  trashOutline,
  createOutline,
  receiptOutline,
  cubeOutline,
  chatbubbleOutline,
  documentTextOutline,
  attachOutline,
  chevronDownOutline,
  checkmark,
  imageOutline,
  closeCircleOutline,
  settingsOutline,
  ellipsisVertical,
} from 'ionicons/icons';
import styles from './ServiceOrderModal.module.scss';
import GlassPicker from './GlassPicker';
import BasePageWithHeader from './BasePageWithHeader';
import MiniCalendarPicker from './MiniCalendarPicker';
import { fetchZohoAll, fetchZohoPricelistItems, resetSoNumberSequence, type ZohoItem, type ZohoTax } from '../services/zohoServiceOrderService';
import type { ZohoPricelist } from '../services/zohoServiceOrderService';
import { getCachedContact } from '@/utils/contactCache';
import { zipToPricelistCode } from '@/utils/pricelists';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { dbContacts } from '../auth/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { intlLocale, dfLocale } from '../i18n/locale';
import { PortalPickerDropdown } from './PortalPickerDropdown';
import ItemPickerSheet from './ItemPickerSheet';

// Per-word match against the visible name only (not name + description — that let items whose invisible description
// happened to separately contain each search word, e.g. "Commercial Gym... Deep Clean" for a "deep cleaning" query,
// outrank / crowd out the actually-relevant items). A single contiguous substring match isn't enough either: "deep
// cleaning 3" doesn't appear as one block in "Deep Cleaning | 3 Bed / 2 Bath..." — each word has to be found
// independently (AND logic), in any order / position within the name.
const itemMatchesQuery = (item: ZohoItem, query: string) => {
  const name = item.name.toLowerCase();
  return query.toLowerCase().split(/\s+/).filter(Boolean).every(t => name.includes(t));
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ServiceOrderLineItem {
  item_id?: string;
  name: string;
  description: string;
  quantity: number;
  rate: number;
  discount?: number;
  discountType?: '%' | '$';
  tax_id?: string;
  tax_name?: string;
}

export interface ServiceOrderDraft {
  soNumber?: string;                     // override Zoho auto-number (blank = let Zoho assign)
  soDate: string;                        // YYYY-MM-DD
  paymentTerms?: number;                 // days: 0 | 3 | 7 | 15 | 30 | 45 | 60
  paymentTermsLabel?: string;            // "Net 3", "Due on Receipt", …
  serviceMonth?: string;                 // "April 2026" — Zoho custom field
  serviceMonthCustomFieldId?: string;    // Zoho customfield_id for reliable mapping
  isInclusiveTax: boolean;
  adjustment?: number;                   // manual adjustment added to subtotal
  pricelistId?: string;                  // Zoho pricelist ID override
  lineItems: ServiceOrderLineItem[];
  notes: string;                         // customer-facing notes
  terms?: string;                        // terms & conditions
  total: number;                         // computed
  taxAmount: number;                     // computed tax portion
  action: 'confirm' | 'draft' | 'send'; // how to process the SO
}

export type { ZohoItem };

interface ServiceOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (draft: ServiceOrderDraft) => void;
  serviceName: string;
  customerName?: string;
  /** Firestore contact ID — used to auto-populate the pricelist (billing doc
   *  default, falling back to a zip-code match) on new orders, same as
   *  ServiceOrderInline.tsx's desktop equivalent. */
  contactId?: string;
  bookingId?: string;
  catalogRate?: number;
  loadingCatalogRate?: boolean;
  /** Error message from Zoho SO creation — highlights the SO# field red */
  soNumberError?: string;
  /** Pre-populate the modal with an existing draft (for editing) */
  initialDraft?: ServiceOrderDraft | null;
  /** YYYY-MM-DD used as the SO date for new orders (e.g. the booking date); defaults to today */
  defaultDate?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PAYMENT_TERMS = [
  { get label() { return i18n.t('components.ServiceOrderModal.dueOnReceipt'); }, days: 0 },
  { get label() { return i18n.t('components.ServiceOrderModal.net3'); },          days: 3 },
  { get label() { return i18n.t('components.ServiceOrderModal.net7'); },          days: 7 },
  { get label() { return i18n.t('components.ServiceOrderModal.net15'); },         days: 15 },
  { get label() { return i18n.t('components.ServiceOrderModal.net30'); },         days: 30 },
  { get label() { return i18n.t('components.ServiceOrderModal.net45'); },         days: 45 },
  { get label() { return i18n.t('components.ServiceOrderModal.net60'); },         days: 60 },
  { get label() { return i18n.t('components.ServiceOrderModal.custom'); },         days: -1 },
];

function getMonthOptions(): string[] {
  const opts: string[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    opts.push(d.toLocaleDateString(intlLocale(), { month: 'long' }));
  }
  return opts;
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function subTotal(items: ServiceOrderLineItem[]): number {
  return items.reduce((s, i) => {
    const lineAmt = i.quantity * i.rate;
    const discountAmt = i.discount
      ? i.discountType === '%' ? lineAmt * (i.discount / 100) : i.discount
      : 0;
    return s + lineAmt - discountAmt;
  }, 0);
}

function emptyLineDraft(): ServiceOrderLineItem {
  return {
    name: '',
    description: '',
    quantity: 1,
    rate: 0,
    discount: undefined,
    discountType: '%',
    tax_id: undefined,
    tax_name: undefined,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────────────────────

const ServiceOrderModal: React.FC<ServiceOrderModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  serviceName: _serviceName,
  customerName = '',
  contactId = '',
  bookingId = '',
  catalogRate: _catalogRate = 0,
  loadingCatalogRate: _loadingCatalogRate = false,
  soNumberError,
  initialDraft,
  defaultDate,
}) => {
  const { t: tr } = useTranslation();
  const [soDate, setSoDate]                     = useState(todayISO());
  const [soNumber, setSoNumber]                 = useState('');
  // 'auto' | 'manual-each' | 'manual-once'
  const [soNumberMode, setSoNumberMode]           = useState<'auto'|'manual-each'|'manual-once'>('auto');
  const [soNumberAlertOpen, setSoNumberAlertOpen] = useState(false);
  const [nextSoNumber, setNextSoNumber]           = useState('');
  const [paymentTermsDays, setPaymentTermsDays] = useState<number>(3);
  const [customTermDays, setCustomTermDays]     = useState<number>(0);
  const [serviceMonth, setServiceMonth]         = useState<string>(getMonthOptions()[0]);
  const [isInclusiveTax, setIsInclusiveTax]     = useState(true);
  const [pricelistId, setPricelistId]             = useState<string>('');
  const [zohoItems, setZohoItems]                 = useState<ZohoItem[]>([]);
  const [baseZohoItems, setBaseZohoItems]         = useState<ZohoItem[]>([]);
  const [loadingItems, setLoadingItems]           = useState(false);
  const [zohoTaxes, setZohoTaxes]                 = useState<ZohoTax[]>([]);
  const [loadingTaxes, setLoadingTaxes]           = useState(false);
  const [serviceMonths, setServiceMonths]         = useState<string[]>(getMonthOptions());
  const [serviceMonthsFromZoho, setServiceMonthsFromZoho] = useState(false);
  const serviceMonthCfIdRef                        = useRef<string>('');

  // Snapshot of props the isOpen-gated fetch effect below reads without
  // becoming reactive to every one of their changes — at least one caller
  // (ServiceOrderDetail.tsx, ServiceOrdersListDesktop.tsx) passes a fresh
  // initialDraft object literal on every render, so depending on it
  // directly would re-run the fetch on every parent render, not just on
  // open.
  const latestForFetchEffectRef = useRef({ contactId, initialDraft });
  useEffect(() => {
    latestForFetchEffectRef.current = { contactId, initialDraft };
  });
  const [editingIndex, setEditingIndex]           = useState<number | null>(null);
  const [termsPickerOpen, setTermsPickerOpen]     = useState(false);
  const [monthPickerOpen, setMonthPickerOpen]     = useState(false);
  const [soDatePickerOpen, setSoDatePickerOpen]   = useState(false);
  const [pricelists, setPricelists]               = useState<ZohoPricelist[]>([]);
  const [loadingPricelists, setLoadingPricelists] = useState(false);
  const [lineItems, setLineItems]               = useState<ServiceOrderLineItem[]>([]);
  const [notes, setNotes]                       = useState('');
  const [terms, setTerms]                       = useState('');
  const [adjustment, setAdjustment]             = useState<string>('');
  const [attachments, setAttachments]           = useState<File[]>([]);
  const [menuOpen, setMenuOpen]                 = useState(false);

  // ── Inline "Add/Edit Line Item" form (replaces the old external LineItemModal) ──
  const [lineFormOpen, setLineFormOpen]         = useState(false);
  const [lineFormData, setLineFormData]         = useState<ServiceOrderLineItem>(emptyLineDraft());
  // Snapshot of the item being edited, captured once when the form opens —
  // compared against the live lineFormData to gate Save on an actual change
  // (not just "form has valid content", which was true from the moment the
  // form opened with the existing item's own data already filled in). Real
  // state (not a ref) since it's read during render to compute isLineItemDirty.
  const [originalLineItem, setOriginalLineItem] = useState<ServiceOrderLineItem | null>(null);
  // Same idea for the whole order form (Save as Draft in the header) — JSON
  // snapshot of what state hydration will settle on, built straight from
  // initialDraft with the exact same fallback rules as the hydration effect
  // below (rather than reading live state right after the effect's setX
  // calls, which would still be the old pre-update values in the same tick).
  // Real state (not a ref) since it's read during render to compute isOrderDirty.
  const [initialSnapshot, setInitialSnapshot] = useState<string | null>(null);
  const [rateManuallyEdited, setRateManuallyEdited] = useState(false);
  // The Item field opens a search sheet of the Zoho catalog; "Custom item" turns it into a text field for an item
  // that is not in the catalog
  const [itemSheetOpen, setItemSheetOpen]       = useState(false);
  const [itemCustom, setItemCustom]             = useState(false);
  const [taxPickerOpen, setTaxPickerOpen]       = useState(false);
  const [pricelistPickerOpen, setPricelistPickerOpen] = useState(false);

  const pickerPortalRef = useRef<HTMLDivElement>(null);
  const paymentTermsFieldRef = useRef<HTMLIonItemElement>(null);
  const serviceMonthFieldRef = useRef<HTMLIonItemElement>(null);
  const pricelistFieldRef = useRef<HTMLIonItemElement>(null);
  const itemFieldRef = useRef<HTMLElement>(null);
  const taxFieldRef = useRef<HTMLIonItemElement>(null);
  const soDateFieldRef = useRef<HTMLIonItemElement>(null);
  const moreButtonRef = useRef<HTMLIonButtonElement>(null);
  const moreMenuCardRef = useRef<HTMLIonCardElement>(null);
  const closeTermsPicker = React.useCallback(() => setTermsPickerOpen(false), []);
  const closeMonthPicker = React.useCallback(() => setMonthPickerOpen(false), []);
  const closeSoDatePicker = React.useCallback(() => setSoDatePickerOpen(false), []);

  // Dismiss the More Options menu on any tap outside it or its trigger.
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: PointerEvent) => {
      const t = e.target as Node;
      if (moreButtonRef.current?.contains(t)) return;
      if (moreMenuCardRef.current?.contains(t)) return;
      setMenuOpen(false);
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [menuOpen]);
  const closePricelistPicker = React.useCallback(() => setPricelistPickerOpen(false), []);
  const closeTaxPicker = React.useCallback(() => setTaxPickerOpen(false), []);

  // Reset/pre-populate the form whenever the modal opens — computed during
  // render (React's "adjust state when a dependency changes" pattern)
  // rather than as synchronous setState calls at the top of the effect
  // below, which now only handles the DOM class toggle and the actual
  // async Zoho fetch.
  const [wasModalOpen, setWasModalOpen] = useState(isOpen);
  if (isOpen !== wasModalOpen) {
    setWasModalOpen(isOpen);
    if (isOpen) {
      setSoDate(defaultDate || todayISO());
      setSoNumber('');
      setSoNumberMode('auto');
      setPaymentTermsDays(3);
      setCustomTermDays(0);
      setServiceMonth(getMonthOptions()[0]);
      setIsInclusiveTax(true);
      setPricelistId('');
      setLineItems([]);
      setNotes('');
      setTerms('');
      setAdjustment('');
      // Pre-populate from initialDraft if provided (edit mode)
      if (initialDraft) {
        if (initialDraft.soDate) setSoDate(initialDraft.soDate);
        if (initialDraft.soNumber) { setSoNumber(initialDraft.soNumber); setSoNumberMode('manual-once'); }
        if (initialDraft.paymentTerms !== undefined) setPaymentTermsDays(initialDraft.paymentTerms);
        if (initialDraft.serviceMonth) setServiceMonth(initialDraft.serviceMonth);
        setIsInclusiveTax(initialDraft.isInclusiveTax ?? true);
        if (initialDraft.pricelistId) setPricelistId(initialDraft.pricelistId);
        if (initialDraft.lineItems?.length) setLineItems(initialDraft.lineItems);
        if (initialDraft.notes) setNotes(initialDraft.notes);
        if (initialDraft.terms) setTerms(initialDraft.terms);
        if (initialDraft.adjustment !== undefined) setAdjustment(String(initialDraft.adjustment));
      }
      // Snapshot of what the hydration above settles on — same fallback rules,
      // computed directly from initialDraft rather than reading state (which
      // wouldn't reflect the setX calls above until after this render commits).
      setInitialSnapshot(initialDraft ? JSON.stringify({
        soDate:            initialDraft.soDate || todayISO(),
        soNumber:          initialDraft.soNumber || '',
        paymentTermsDays:  initialDraft.paymentTerms !== undefined ? initialDraft.paymentTerms : 3,
        serviceMonth:      initialDraft.serviceMonth || getMonthOptions()[0],
        isInclusiveTax:    initialDraft.isInclusiveTax ?? true,
        pricelistId:       initialDraft.pricelistId || '',
        lineItems:         initialDraft.lineItems?.length ? initialDraft.lineItems : [],
        notes:             initialDraft.notes || '',
        terms:             initialDraft.terms || '',
        adjustment:        initialDraft.adjustment !== undefined ? String(initialDraft.adjustment) : '',
      }) : null);
      // Single call fetches everything — avoids Zoho token rate limiting
      setLoadingItems(true);
      setLoadingTaxes(true);
      setLoadingPricelists(true);
    }
  }

  useEffect(() => {
    if (!isOpen) {
      document.body.classList.remove('service-order-modal-open');
      return;
    }
    document.body.classList.add('service-order-modal-open');
    const { contactId, initialDraft } = latestForFetchEffectRef.current;
    // Auto-populate the pricelist on brand-new orders (never on an edit —
    // initialDraft already carries the order's real pricelist) from the
    // contact's billing default, falling back to a zip-code match — same
    // logic as ServiceOrderInline.tsx's desktop equivalent. Without this,
    // this form fell back to base catalog rates instead of the customer's
    // actual pricelist rates, showing a different price than desktop for the
    // same item. Runs as its own background fetch (not bundled into the
    // fetchZohoAll Promise.allSettled below) — bundling it delayed the whole
    // item list behind a slower contact/billing lookup, when the list itself
    // is ready and browsable well before that resolves.
    const shouldAutoPopulate = !!contactId && !initialDraft;
    const svc = DataServiceFactory.getService();
    console.log('[SO] fetchZohoAll starting...');
    fetchZohoAll()
      .then(all => {
        console.log('[SO] fetchZohoAll success — items:', all.items.length, 'taxes:', all.taxes.length, 'pricelists:', all.pricelists.length, 'defaults:', all.defaults);
        setZohoItems(all.items);
        setBaseZohoItems(all.items);
        setZohoTaxes(all.taxes);
        setPricelists(all.pricelists);
        const months = all.serviceMonthOptions.length ? all.serviceMonthOptions : getMonthOptions();
        setServiceMonths(months);
        setServiceMonthsFromZoho(all.serviceMonthOptions.length > 0);
        // Default to current month name if it's in the list, else first option
        // — only when not already pre-populated from initialDraft (edit mode),
        // same guard as notes/terms below; this ran unconditionally before,
        // silently overwriting an existing order's real service month.
        if (!initialDraft?.serviceMonth) {
          const currentMonthName = new Date().toLocaleDateString('en-US', { month: 'long' }); // i18n-ignore: saved to Zoho, stays English
          const defaultMonth = months.find(m => m === currentMonthName) ?? months[0] ?? '';
          setServiceMonth(defaultMonth);
        }
        serviceMonthCfIdRef.current = all.serviceMonthCustomFieldId ?? '';
        if (all.defaults.nextSoNumber) {
          setNextSoNumber(all.defaults.nextSoNumber.replace(/^SO-/i, ''));
        }
        // Only apply Zoho defaults for notes/terms if not pre-populated from initialDraft
        if (!initialDraft?.notes && all.defaults.notes) setNotes(all.defaults.notes);
        if (!initialDraft?.terms && all.defaults.terms) setTerms(all.defaults.terms);

        if (!shouldAutoPopulate) return;

        Promise.allSettled([
          getCachedContact(contactId, () => svc.contacts.getContactById(contactId)),
          getDoc(doc(dbContacts, 'contactBilling', contactId)),
        ]).then(([contactSettled, billingSettled]) => {
          const contactResult = contactSettled.status === 'fulfilled' ? contactSettled.value : null;
          const billingSnap   = billingSettled.status === 'fulfilled' ? billingSettled.value : null;
          const billing = billingSnap && 'exists' in billingSnap && billingSnap.exists() ? billingSnap.data() : null;

          // Pricelist: billing doc wins, fallback to zip lookup
          if (billing?.defaultPricelistId) {
            setPricelistId(billing.defaultPricelistId);
          } else {
            const addresses = (contactResult as { data?: { addresses?: { primary?: boolean; address?: { postalCode?: string } }[] } } | null)?.data?.addresses ?? [];
            const primary = addresses.find((a) => a.primary) ?? addresses[0];
            const zip = primary?.address?.postalCode;
            const code = zip ? zipToPricelistCode(zip) : null;
            const match = code ? all.pricelists.find(p => p.name === code) : null;
            if (match) setPricelistId(match.pricelist_id);
          }

          // The rest of the contact's billing config. Only the pricelist used to be applied here,
          // so a new booking ignored the contact's tax setting, default item and default rate
          // (the desktop's inline editor applies them all).
          if (billing?.isInclusiveTax !== undefined) setIsInclusiveTax(billing.isInclusiveTax);

          if (billing?.defaultItemId) {
            const item = all.items.find(i => i.item_id === billing.defaultItemId);
            if (item) {
              // billing.defaultRate (the Billing Config "Rate Override") is always
              // "the final price the customer pays," per the billing config UI —
              // NOT the Zoho line-item rate directly. The two only match when tax
              // is Inclusive. When Exclusive AND this client is flagged as a
              // recurring Helcim subscriber, defaultRate must be back-calculated
              // to the pre-tax rate Zoho needs (Zoho then adds tax on top to
              // reach that same final price) — mirrors
              // functions/src/services/zohoServiceOrderService.ts's
              // finalPriceToRate, which the Wix-booking sync path already uses.
              // Confirmed real bug (2026-10-13+): this screen skipped that
              // conversion entirely, so a subscriber's $200 override showed as
              // rate=$200 with tax added ON TOP (total $221.10) instead of
              // rate=$180.91 (total $200, matching Helcim's own invoice).
              // Non-subscriber Exclusive clients are unaffected — for them
              // defaultRate IS meant to be used as the pre-tax rate as-is.
              const isSubscriberExclusive = billing.isInclusiveTax === false && billing.isHelcimSubscriber === true;
              const taxPct = typeof billing.taxPercentage === 'number' ? billing.taxPercentage : 0;
              const rawRate = billing.defaultRate != null && billing.defaultRate > 0 ? billing.defaultRate : item.rate;
              const rate = (isSubscriberExclusive && taxPct > 0 && billing.defaultRate != null && billing.defaultRate > 0)
                ? Math.round((rawRate / (1 + taxPct / 100)) * 100) / 100
                : rawRate;
              // Tax: the contact's own choice. "exempt" = non-taxable (tax_id ''); otherwise their
              // saved tax, else the same default the "Add item" form picks.
              const fallbackTax =
                all.taxes.find(t => t.is_tax_group && t.tax_name.toLowerCase().includes('washington sales tax') && !t.tax_name.toLowerCase().includes('2025')) ??
                all.taxes.find(t => t.is_tax_group) ??
                all.taxes[0];
              const exempt = billing.taxPreference === 'exempt';
              const taxId = exempt ? '' : (billing.taxId || fallbackTax?.tax_id);
              const taxName = exempt ? undefined : (billing.taxName || all.taxes.find(t => t.tax_id === taxId)?.tax_name || fallbackTax?.tax_name);
              setLineItems([{
                item_id: item.item_id,
                name: item.name,
                description: item.description ?? '',
                quantity: 1,
                rate,
                tax_id: taxId,
                tax_name: taxName,
              }]);
            }
          }
        });
      })
      .catch(e => {
        console.error('[SO] fetchZohoAll FAILED:', e?.message ?? e, e?.code, e?.details);
        setServiceMonths(getMonthOptions());
      })
      .finally(() => {
        setLoadingItems(false);
        setLoadingTaxes(false);
        setLoadingPricelists(false);
      });
    return () => document.body.classList.remove('service-order-modal-open');
  }, [isOpen]);

  // When pricelist changes, merge pricebook rates into items. Falling back
  // to the base catalog when no pricelist is selected is computed during
  // render (React's "adjust state when a dependency changes" pattern)
  // rather than as a synchronous setState call at the top of the effect,
  // which now only handles the actual async fetch.
  const [prevPricelistInputs, setPrevPricelistInputs] = useState({ pricelistId, baseZohoItems });
  if (pricelistId !== prevPricelistInputs.pricelistId || baseZohoItems !== prevPricelistInputs.baseZohoItems) {
    setPrevPricelistInputs({ pricelistId, baseZohoItems });
    if (!pricelistId) setZohoItems(baseZohoItems);
  }
  useEffect(() => {
    if (!pricelistId) return;
    fetchZohoPricelistItems(pricelistId)
      .then(plItems => {
        if (!plItems.length) return;
        const rateMap = new Map(plItems.map(i => [i.item_id, i.rate]));
        setZohoItems(baseZohoItems.map(i =>
          rateMap.has(i.item_id) ? { ...i, rate: rateMap.get(i.item_id)! } : i
        ));
      })
      .catch(() => setZohoItems(baseZohoItems));
  }, [pricelistId, baseZohoItems]);

  // When zohoItems updates (pricelist rate merge), refresh the open form's
  // rate ONLY if the user hasn't manually overridden it. Computed during
  // render for the same reason as above — it's a pure re-derivation from
  // already-available state with no external system involved.
  const [prevZohoItemsForRate, setPrevZohoItemsForRate] = useState(zohoItems);
  if (zohoItems !== prevZohoItemsForRate) {
    setPrevZohoItemsForRate(zohoItems);
    if (lineFormOpen && lineFormData.item_id && !rateManuallyEdited) {
      const zi = zohoItems.find(z => z.item_id === lineFormData.item_id);
      if (zi && zi.rate !== lineFormData.rate) {
        setLineFormData(prev => ({ ...prev, rate: zi.rate }));
      }
    }
  }

  const openAddItem = () => {
    setEditingIndex(null);
    setOriginalLineItem(null);
    setRateManuallyEdited(false);
    setItemCustom(false);
    // Prefer "Washington Sales Tax" tax group, fall back to first tax group, then first tax
    const defaultTax =
      zohoTaxes.find(t => t.is_tax_group && t.tax_name.toLowerCase().includes('washington sales tax') && !t.tax_name.toLowerCase().includes('2025')) ??
      zohoTaxes.find(t => t.is_tax_group) ??
      zohoTaxes[0];
    setLineFormData({ ...emptyLineDraft(), tax_id: defaultTax?.tax_id, tax_name: defaultTax?.tax_name });
    setLineFormOpen(true);
  };

  const openEditItem = (idx: number) => {
    setEditingIndex(idx);
    setRateManuallyEdited(false);
    // A line that did not come from the catalog is a custom item: its name is editable text
    setItemCustom(!lineItems[idx].item_id);
    setOriginalLineItem({ ...lineItems[idx] });
    setLineFormData({ ...lineItems[idx] });
    setLineFormOpen(true);
  };

  // Same fallback as canConfirm/the read-only order view (l.name ?? l.description)
  // — some Zoho line items carry their text in description only, with an
  // empty name, which permanently blocked Save if only name was checked.
  const hasValidLineContent = lineFormData.name.trim().length > 0 || lineFormData.description.trim().length > 0;
  // Editing an existing item (originalLineItemRef set) should stay disabled
  // until something actually changed — valid content alone was already true
  // the instant the form opened with the item's own existing data filled in.
  // Adding a brand-new item has no "original" to diff against, so any valid
  // content is enough there.
  const isLineItemDirty = !originalLineItem
    || JSON.stringify(lineFormData) !== JSON.stringify(originalLineItem);
  const canSaveLineForm = hasValidLineContent && isLineItemDirty;

  const saveLineForm = (addAnother: boolean) => {
    if (!canSaveLineForm) return;
    if (editingIndex !== null) {
      setLineItems(prev => prev.map((li, i) => i === editingIndex ? lineFormData : li));
    } else {
      setLineItems(prev => [...prev, lineFormData]);
    }
    setEditingIndex(null);
    if (addAnother) {
      setRateManuallyEdited(false);
      setItemCustom(false);
      setLineFormData(emptyLineDraft());
    } else {
      setLineFormOpen(false);
    }
  };

  const removeItem = (idx: number) => setLineItems(prev => prev.filter((_, i) => i !== idx));

  // For inclusive tax + flat $ discount, Zoho applies the discount to the pre-tax
  // portion: total = rate*qty - discount*(1 + taxPct/100).
  // All other cases: standard rate*qty - discountAmt.
  const total = (() => {
    if (!isInclusiveTax) return subTotal(lineItems);
    return lineItems.reduce((s, item) => {
      const lineAmt = item.quantity * item.rate;
      if (!item.discount) return s + lineAmt;
      if (item.discountType !== '$') return s + lineAmt * (1 - item.discount / 100);
      const taxInfo = zohoTaxes.find(t => t.tax_id === item.tax_id);
      const taxPct = taxInfo?.tax_percentage ?? 0;
      return s + lineAmt - item.discount * (1 + taxPct / 100);
    }, 0);
  })();
  const adjustmentNum = parseFloat(adjustment) || 0;

  // ── Compute tax breakdown for use in summary rows ──────────────────────────
  const taxBreakdownMap = (() => {
    const taxMap = new Map<string, { name: string; pct: number; amount: number }>();
    for (const item of lineItems) {
      if (!item.tax_id) continue;
      const lineAmt = item.quantity * item.rate;
      const discountAmt = item.discount
        ? item.discountType === '%' ? lineAmt * (item.discount / 100) : item.discount
        : 0;
      const net = lineAmt - discountAmt;
      const taxInfo = zohoTaxes.find(t => t.tax_id === item.tax_id);
      // For inclusive tax, pre-tax net is needed to compute each component's tax:
      //   flat $: preTaxNet = lineAmt / totalTaxFactor - discount
      //   %:      preTaxNet = net / totalTaxFactor
      const groupOrSinglePct = taxInfo?.tax_percentage ?? 0;
      const preTaxNet = isInclusiveTax
        ? (item.discountType === '$' && item.discount
            ? lineAmt / (1 + groupOrSinglePct / 100) - item.discount
            : net / (1 + groupOrSinglePct / 100))
        : net;
      if (taxInfo?.is_tax_group && taxInfo.taxes?.length) {
        for (const comp of taxInfo.taxes) {
          const compAmt = isInclusiveTax
            ? preTaxNet * (comp.tax_percentage / 100)
            : net * (comp.tax_percentage / 100);
          const existing = taxMap.get(comp.tax_id);
          if (existing) { existing.amount += compAmt; }
          else { taxMap.set(comp.tax_id, { name: `${comp.tax_name} [${comp.tax_percentage}%]`, pct: comp.tax_percentage, amount: compAmt }); }
        }
      } else {
        const pct = taxInfo?.tax_percentage ?? 0;
        const taxAmt = isInclusiveTax
          ? preTaxNet * (pct / 100)
          : net * (pct / 100);
        const existing = taxMap.get(item.tax_id);
        if (existing) { existing.amount += taxAmt; }
        else {
          const label = taxInfo ? `${taxInfo.tax_name} [${taxInfo.tax_percentage}%]` : (item.tax_name ?? item.tax_id);
          taxMap.set(item.tax_id, { name: label, pct, amount: taxAmt });
        }
      }
    }
    return taxMap;
  })();
  const taxBreakdownRows = Array.from(taxBreakdownMap.values());
  const totalTaxAmt = taxBreakdownRows.reduce((s, r) => s + r.amount, 0);
  // Sub Total: always the after-discount line amount.
  //   Inclusive: tax is embedded in this number (shown as "Tax Inclusive")
  //   Exclusive: tax is added on top below
  const subTotalAmt = total;
  // Grand Total: exclusive adds tax on top; inclusive tax is already in line prices
  const grandTotal = isInclusiveTax ? total + adjustmentNum : total + totalTaxAmt + adjustmentNum;
  const effectiveTermDays = paymentTermsDays === -1 ? customTermDays : paymentTermsDays;
  const termLabel = paymentTermsDays === -1
    ? (customTermDays > 0 ? tr('components.ServiceOrderModal.net', { customTermDays }) : tr('components.ServiceOrderModal.custom'))
    : (PAYMENT_TERMS.find(t => t.days === paymentTermsDays)?.label ?? tr('components.ServiceOrderModal.net3'));

  // Editing an existing order (initialDraft provided) already has valid,
  // individually-priced/taxed line items pulled straight from Zoho — a
  // pricelist is only needed to auto-suggest rates for NEW line items, not to
  // save an order that already has its own rates set, so it isn't required here.
  const isEditMode = !!initialDraft;
  // Editing should stay disabled until something actually changed — valid
  // content alone was already true the instant the form opened with the
  // existing order's own data already filled in. A brand-new order has no
  // "original" to diff against (initialSnapshot is null then).
  const isOrderDirty = !initialSnapshot || JSON.stringify({
    soDate, soNumber, paymentTermsDays, serviceMonth, isInclusiveTax,
    pricelistId, lineItems, notes, terms, adjustment,
  }) !== initialSnapshot;
  const canConfirm =
    !!customerName?.trim() &&
    !!soDate?.trim() &&
    !!serviceMonth?.trim() &&
    (isEditMode || !!pricelistId?.trim()) &&
    isOrderDirty &&
    lineItems.length > 0 &&
    // Some Zoho SO line items carry their text in `description` only, with an
    // empty `name` (same fallback the read-only order view already uses:
    // `l.name ?? l.description`) — checking `name` alone here wrongly treated
    // those as blank/invalid items and permanently blocked Save.
    lineItems.some(i => i.name.trim() || i.description.trim()) &&
    // Same Zoho SO quirk as the name/description fallback above — some line
    // items carry a human-readable `tax_name` ("Washington Sales Tax") but
    // never got a `tax_id` extracted, so require one or the other rather than
    // specifically the raw ID, or a pre-existing, already-taxed edit-mode
    // item permanently blocks Save for a field the UI doesn't even surface.
    !lineItems.some(i => (i.name.trim() || i.description.trim()) && !i.tax_id && !i.tax_name);

  const handleConfirm = (action: 'confirm' | 'draft' | 'send') => {
    onConfirm({
      soNumber:          soNumberMode !== 'auto' && soNumber.trim() && soNumber.trim() !== 'SO-' ? soNumber.trim() : undefined,
      soDate,
      paymentTerms:      effectiveTermDays,
      paymentTermsLabel: termLabel,
      serviceMonth:              serviceMonthsFromZoho ? (serviceMonth || undefined) : undefined,
      serviceMonthCustomFieldId: serviceMonthCfIdRef.current || undefined,
      isInclusiveTax,
      adjustment:        adjustmentNum || undefined,
      pricelistId:       pricelistId.trim() || undefined,
      lineItems,
      notes,
      terms:             terms          || undefined,
      total:             grandTotal,
      taxAmount:         totalTaxAmt,
      action,
    });
    onClose();
  };

  return (
    <>
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      showBackdrop={true}
      backdropDismiss={true}
      canDismiss={true}
      className={styles.modal}
      id="service-order-modal"
    >
      {/* ── Header ─────────────────────────────────────────────────── */}
      <BasePageWithHeader
        title={initialDraft?.lineItems?.length ? tr('components.ServiceOrderModal.editServiceOrder') : tr('components.ServiceOrderModal.newServiceOrder')}
        toolbarClassName={styles.toolbar}
        startButtons={
          <IonButton fill="clear" className={styles.closeBtn} onClick={onClose}>
            <IonIcon slot="icon-only" icon={closeOutline} />
          </IonButton>
        }
        endButtons={
          <>
            <IonButton
              fill="solid"
              className={styles.confirmBtn}
              disabled={!canConfirm}
              onClick={() => handleConfirm('draft')}
            >{tr('components.ServiceOrderModal.saveAsDraft')}
            </IonButton>
            <IonButton
              ref={moreButtonRef}
              fill="clear"
              className={styles.menuBtn}
              onClick={() => setMenuOpen(o => !o)}
            >
              <IonIcon slot="icon-only" icon={ellipsisVertical} />
            </IonButton>
          </>
        }
      />

      <IonContent className={styles.content}>
        <IonGrid className={styles.gridContainer}>

        {/* Portal target for the field dropdowns below */}
        <div ref={pickerPortalRef} className={styles.pickerPortalRoot} />
        <ItemPickerSheet
          isOpen={itemSheetOpen}
          onClose={() => setItemSheetOpen(false)}
          items={zohoItems}
          matches={itemMatchesQuery}
          formatRate={(rate) => `$${rate.toFixed(2)}`}
          onSelect={(zi) => {
            setRateManuallyEdited(false);
            setLineFormData(prev => ({
              ...prev,
              item_id: zi.item_id,
              name: zi.name,
              rate: zi.rate,
              description: zi.description ?? '',
            }));
            setItemCustom(false);
          }}
          onCustomItem={() => {
            setItemSheetOpen(false);
            setItemCustom(true);
            setTimeout(() => { void (itemFieldRef.current as HTMLIonInputElement | null)?.setFocus(); }, 350); // after the sheet has slid away
          }}
        />

        {/* ── Section 1: Header Info — one card, formGrid of formGroups,
             exactly matching CreateInvoice.tsx's isServiceOrder Details card
             (.card > .cardBody > .cardTitle + .formGrid > .formGroup×N).
             Each .formGroup is an unboxed label-above-value stack; only the
             value itself (.input / the picker trigger) carries the border —
             not the label, and not the group as a whole. ────────────────── */}
        <IonCard className={styles.card}>
          <IonCardContent className={styles.cardBody}>
            <IonLabel className={styles.cardTitle}>
              <IonIcon icon={receiptOutline} />{tr('components.ServiceOrderModal.orderDetails')}
            </IonLabel>

            <IonLabel className={styles.formGrid}>
              <IonLabel className={styles.formGroup}>
                <span className={styles.fieldLabel}>{tr('components.ServiceOrderModal.customerName')} <span className={styles.req}>*</span></span>
                <IonInput value={customerName || 'Guest'} readonly className={styles.input} />
              </IonLabel>

              <IonLabel className={styles.formGroup}>
                <span className={styles.fieldLabel} style={soNumberError ? { color: 'var(--ion-color-danger)' } : undefined}>{tr('components.ServiceOrderModal.serviceOrder')}
                </span>
                <IonLabel className={styles.inputRow}>
                  <IonInput
                    value={soNumberMode === 'auto' ? (nextSoNumber ? 'SO-' + nextSoNumber : '') : soNumber}
                    placeholder={soNumberMode === 'manual-once' ? tr('components.ServiceOrderModal.so') : tr('components.ServiceOrderModal.autoAssignedByZoho')}
                    readonly={soNumberMode !== 'manual-once'}
                    className={`${styles.input} ${styles.inputFlex}`}
                    color={soNumberError ? 'danger' : undefined}
                    onClick={() => { if (soNumberMode !== 'manual-once') setSoNumberAlertOpen(true); }}
                    onIonInput={e => {
                      if (soNumberMode !== 'manual-once') return;
                      let val = String(e.detail.value ?? '');
                      if (val && !val.startsWith('SO-')) val = 'SO-' + val.replace(/^SO-*/i, '');
                      setSoNumber(val);
                    }}
                  />
                  <IonButton fill="clear" className={styles.soSettingsBtn} onClick={() => setSoNumberAlertOpen(true)}>
                    <IonIcon icon={settingsOutline} slot="icon-only" />
                  </IonButton>
                </IonLabel>
                {soNumberError && <p className={styles.fieldError}>{soNumberError}</p>}
              </IonLabel>

              <IonLabel className={styles.formGroup}>
                <span className={styles.fieldLabel}>{tr('components.ServiceOrderModal.referenceBookingId')}</span>
                <IonInput value={bookingId} readonly placeholder={tr('components.ServiceOrderModal.wixBookingId')} className={styles.input} />
              </IonLabel>

              <IonLabel className={styles.formGroup}>
                <span className={styles.fieldLabel}>{tr('components.ServiceOrderModal.serviceOrderDate')} <span className={styles.req}>*</span></span>
                <IonItem button detail={false} lines="none" ref={soDateFieldRef} className={styles.selectField} onClick={() => setSoDatePickerOpen(o => !o)}>
                  <IonLabel className={styles.selectFieldValue}>
                    <span>{format(new Date(`${soDate}T00:00:00`), 'EEE, MMM d, yyyy', { locale: dfLocale() })}</span>
                  </IonLabel>
                  <IonIcon icon={chevronDownOutline} slot="end" className={`${styles.selectCaret} ${soDatePickerOpen ? styles.selectCaretOpen : ''}`} />
                </IonItem>
                <PortalPickerDropdown isOpen={soDatePickerOpen} fieldRef={soDateFieldRef} portalRef={pickerPortalRef} close={closeSoDatePicker} align="left" className={styles.calendarDropdown}>
                  <MiniCalendarPicker
                    desktop
                    value={new Date(`${soDate}T00:00:00`)}
                    onSelect={d => { setSoDate(format(d, 'yyyy-MM-dd')); setSoDatePickerOpen(false); }}
                  />
                </PortalPickerDropdown>
              </IonLabel>

              <IonLabel className={styles.formGroup}>
                <span className={styles.fieldLabel}>{tr('components.ServiceOrderModal.paymentTerms')}</span>
                <IonItem button detail={false} lines="none" ref={paymentTermsFieldRef} className={styles.selectField} onClick={() => setTermsPickerOpen(o => !o)}>
                  <IonLabel className={styles.selectFieldValue}><span>{termLabel}</span></IonLabel>
                  <IonIcon icon={chevronDownOutline} slot="end" className={`${styles.selectCaret} ${termsPickerOpen ? styles.selectCaretOpen : ''}`} />
                </IonItem>
                <PortalPickerDropdown isOpen={termsPickerOpen} fieldRef={paymentTermsFieldRef} portalRef={pickerPortalRef} close={closeTermsPicker} align="left" matchWidth exactWidth className={styles.calendarDropdown}>
                  <IonList lines="none" className={styles.dropdownList}>
                    {PAYMENT_TERMS.map(t => (
                      <IonItem
                        key={t.days}
                        button
                        detail={false}
                        lines="none"
                        className={`${styles.dropdownItem}${paymentTermsDays === t.days ? ` ${styles.dropdownItemActive}` : ''}`}
                        onClick={() => { setPaymentTermsDays(t.days); setTermsPickerOpen(false); }}
                      >
                        <IonLabel><h3>{t.label}</h3></IonLabel>
                        {paymentTermsDays === t.days && <IonIcon icon={checkmark} slot="end" />}
                      </IonItem>
                    ))}
                  </IonList>
                </PortalPickerDropdown>
              </IonLabel>

              {paymentTermsDays === -1 && (
                <IonLabel className={styles.formGroup}>
                  <span className={styles.fieldLabel}>{tr('components.ServiceOrderModal.customDaysNet')}</span>
                  <IonInput
                    type="number"
                    min="1"
                    placeholder="e.g. 45"
                    value={customTermDays || ''}
                    onIonInput={e => setCustomTermDays(Number(e.detail.value ?? 0))}
                    className={styles.input}
                  />
                </IonLabel>
              )}

              <IonLabel className={styles.formGroup}>
                <span className={styles.fieldLabel}>{tr('components.ServiceOrderModal.serviceMonth')} <span className={styles.req}>*</span></span>
                <IonItem button detail={false} lines="none" ref={serviceMonthFieldRef} className={styles.selectField} onClick={() => setMonthPickerOpen(o => !o)}>
                  <IonLabel className={`${styles.selectFieldValue} ${!serviceMonth ? styles.selectPlaceholder : ''}`}><span>{serviceMonth || tr('components.ServiceOrderModal.none')}</span></IonLabel>
                  <IonIcon icon={chevronDownOutline} slot="end" className={`${styles.selectCaret} ${monthPickerOpen ? styles.selectCaretOpen : ''}`} />
                </IonItem>
                <PortalPickerDropdown isOpen={monthPickerOpen} fieldRef={serviceMonthFieldRef} portalRef={pickerPortalRef} close={closeMonthPicker} align="left" matchWidth exactWidth className={styles.calendarDropdown}>
                  <IonList lines="none" className={styles.dropdownList}>
                    <IonItem button detail={false} lines="none" className={`${styles.dropdownItem}${!serviceMonth ? ` ${styles.dropdownItemActive}` : ''}`} onClick={() => { setServiceMonth(''); setMonthPickerOpen(false); }}>
                      <IonLabel><h3>{tr('components.ServiceOrderModal.none')}</h3></IonLabel>
                      {!serviceMonth && <IonIcon icon={checkmark} slot="end" />}
                    </IonItem>
                    {serviceMonths.map(m => (
                      <IonItem
                        key={m}
                        button
                        detail={false}
                        lines="none"
                        className={`${styles.dropdownItem}${serviceMonth === m ? ` ${styles.dropdownItemActive}` : ''}`}
                        onClick={() => { setServiceMonth(m); setMonthPickerOpen(false); }}
                      >
                        <IonLabel><h3>{m}</h3></IonLabel>
                        {serviceMonth === m && <IonIcon icon={checkmark} slot="end" />}
                      </IonItem>
                    ))}
                  </IonList>
                </PortalPickerDropdown>
              </IonLabel>

              <IonLabel className={styles.formGroup}>
                <span className={styles.fieldLabel}>{tr('components.ServiceOrderModal.tax')}</span>
                <IonRadioGroup
                  value={isInclusiveTax ? 'inclusive' : 'exclusive'}
                  onIonChange={e => setIsInclusiveTax(e.detail.value === 'inclusive')}
                  className={styles.radioRow}
                >
                  <IonRadio value="exclusive" className={styles.radioOption}>{tr('components.ServiceOrderModal.exclusive')}</IonRadio>
                  <IonRadio value="inclusive" className={styles.radioOption}>{tr('components.ServiceOrderModal.inclusive')}</IonRadio>
                </IonRadioGroup>
              </IonLabel>
            </IonLabel>
          </IonCardContent>
        </IonCard>

        {/* ── Section 4: Line Items ──────────────────────────────── */}
        <IonCard className={styles.card}>
          <IonCardContent className={styles.cardBody}>
            <IonLabel className={styles.cardTitle}>
              <IonIcon icon={cubeOutline} />{tr('components.ServiceOrderModal.lineItems')}
              {lineItems.length > 0 && (
                <IonButton fill="clear" className={styles.clearAllBtn} onClick={() => setLineItems([])}>{tr('components.ServiceOrderModal.clearAll')}
                </IonButton>
              )}
            </IonLabel>

            <IonLabel className={styles.formGroup} style={{ marginBottom: 14 }}>
              <span className={styles.fieldLabel}>{tr('components.ServiceOrderModal.priceList')} <span className={styles.req}>*</span></span>
              <IonItem button detail={false} lines="none" ref={pricelistFieldRef} className={styles.selectField} disabled={loadingPricelists} onClick={() => setPricelistPickerOpen(o => !o)}>
                <IonLabel className={`${styles.selectFieldValue} ${!pricelistId ? styles.selectPlaceholder : ''}`}>
                  <span>{loadingPricelists ? tr('components.ServiceOrderModal.loading') : pricelistId ? (pricelists.find(p => p.pricelist_id === pricelistId)?.name ?? pricelistId) : tr('components.ServiceOrderModal.selectPriceList')}</span>
                </IonLabel>
                <IonIcon icon={chevronDownOutline} slot="end" className={`${styles.selectCaret} ${pricelistPickerOpen ? styles.selectCaretOpen : ''}`} />
              </IonItem>
              <PortalPickerDropdown isOpen={pricelistPickerOpen} fieldRef={pricelistFieldRef} portalRef={pickerPortalRef} close={closePricelistPicker} align="left" matchWidth exactWidth className={styles.calendarDropdown}>
                <IonList lines="none" className={styles.dropdownList}>
                  <IonItem button detail={false} lines="none" className={`${styles.dropdownItem}${!pricelistId ? ` ${styles.dropdownItemActive}` : ''}`} onClick={() => { setPricelistId(''); setPricelistPickerOpen(false); }}>
                    <IonLabel><h3>{tr('components.ServiceOrderModal.none')}</h3></IonLabel>
                    {!pricelistId && <IonIcon icon={checkmark} slot="end" />}
                  </IonItem>
                  {pricelists.map(p => (
                    <IonItem
                      key={p.pricelist_id}
                      button
                      detail={false}
                      lines="none"
                      className={`${styles.dropdownItem}${pricelistId === p.pricelist_id ? ` ${styles.dropdownItemActive}` : ''}`}
                      onClick={() => { setPricelistId(p.pricelist_id); setPricelistPickerOpen(false); }}
                    >
                      <IonLabel><h3>{p.name}{p.currency_code ? ` (${p.currency_code})` : ''}</h3></IonLabel>
                      {pricelistId === p.pricelist_id && <IonIcon icon={checkmark} slot="end" />}
                    </IonItem>
                  ))}
                </IonList>
              </PortalPickerDropdown>
            </IonLabel>

            {lineItems.map((item, index) => {
              const lineAmt = item.quantity * item.rate;
              const rowTaxPct = zohoTaxes.find(t => t.tax_id === item.tax_id)?.tax_percentage ?? 0;
              const netAmt = (() => {
                if (!item.discount) return lineAmt;
                if (!isInclusiveTax || item.discountType !== '$') {
                  const d = item.discountType === '%' ? lineAmt * (item.discount / 100) : item.discount;
                  return lineAmt - d;
                }
                return lineAmt - item.discount * (1 + rowTaxPct / 100);
              })();
              return (
                <IonLabel key={index} className={styles.lineItemRow}>
                  <IonLabel className={styles.lineItemMain}>
                    <IonLabel>
                      <span className={styles.lineItemName}>{item.name}</span>
                      {item.description.trim() && (
                        <IonLabel className={styles.lineItemDesc}>{item.description}</IonLabel>
                      )}
                    </IonLabel>
                    <IonLabel style={{ display: 'flex', gap: 4 }}>
                      <IonButton fill="clear" className={styles.lineItemDelete} onClick={() => openEditItem(index)}>
                        <IonIcon icon={createOutline} />
                      </IonButton>
                      <IonButton fill="clear" className={styles.lineItemDelete} onClick={() => removeItem(index)}>
                        <IonIcon icon={trashOutline} />
                      </IonButton>
                    </IonLabel>
                  </IonLabel>
                  <IonLabel className={styles.lineItemSub}>
                    <span className={styles.lineItemQtyBadge}>{item.quantity}x</span>
                    <span className={styles.lineItemPrice}>${netAmt.toFixed(2)}</span>
                    {item.discount ? (
                      <span className={styles.lineItemDiscount}>
                        -{item.discountType === '$' ? `$${item.discount}` : `${item.discount}%`}
                      </span>
                    ) : null}
                    {item.tax_name ? <span className={styles.lineItemTax}>{item.tax_name}</span> : null}
                  </IonLabel>
                </IonLabel>
              );
            })}

            {lineFormOpen ? (
              <IonLabel className={styles.lineFormPanel}>
                <IonLabel className={styles.lineFormTitle}>{editingIndex !== null ? tr('components.ServiceOrderModal.editLineItem') : tr('components.ServiceOrderModal.addLineItem')}</IonLabel>

                <IonLabel className={styles.formGroup}>
                  <span className={styles.fieldLabel}>{tr('components.ServiceOrderModal.item')}{editingIndex === null ? ' *' : ''}</span>
                  <IonInput
                    ref={(el) => { itemFieldRef.current = el as unknown as HTMLElement; }}
                    className={styles.input}
                    value={lineFormData.name}
                    readonly={!itemCustom}
                    disabled={loadingItems}
                    placeholder={tr('components.ServiceOrderModal.searchItem')}
                    onClick={() => { if (!itemCustom) setItemSheetOpen(true); }}
                    // In picker mode the field only opens the list — it must not keep focus: a focused field behind the
                    // sheet makes the native webview scroll the whole page (and the sheet with it) to reveal it
                    onIonFocus={e => { if (!itemCustom) void (e.target as HTMLIonInputElement).getInputElement().then(el => el.blur()); }}
                    // Custom mode is only for typing a name: leaving the field empty goes back to picking from the list
                    onIonBlur={() => { if (itemCustom && !lineFormData.name.trim()) setItemCustom(false); }}
                    onIonInput={e => setLineFormData(prev => ({ ...prev, name: String(e.detail.value ?? '') }))}
                  />
                </IonLabel>

                <IonLabel className={styles.formGroup}>
                  <span className={styles.fieldLabel}>{tr('components.ServiceOrderModal.description')}</span>
                  <IonTextarea
                    rows={2}
                    autoGrow
                    placeholder={tr('components.ServiceOrderModal.addADescriptionForYourItem')}
                    value={lineFormData.description}
                    onIonInput={e => setLineFormData(prev => ({ ...prev, description: String(e.detail.value ?? '') }))}
                    className={styles.textarea}
                  />
                </IonLabel>

                <IonLabel className={styles.lineFormRow}>
                  <IonLabel className={styles.formGroup}>
                    <span className={styles.fieldLabel}>{tr('components.ServiceOrderModal.quantity')}</span>
                    <IonInput
                      type="number"
                      min="0"
                      step="0.01"
                      value={lineFormData.quantity}
                      onIonInput={e => setLineFormData(prev => ({ ...prev, quantity: Number(e.detail.value ?? 1) || 1 }))}
                      className={styles.input}
                    />
                  </IonLabel>
                  <IonLabel className={styles.formGroup}>
                    <span className={styles.fieldLabel}>{tr('components.ServiceOrderModal.rate')}</span>
                    <IonInput
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0"
                      value={lineFormData.rate || ''}
                      onIonInput={e => { setLineFormData(prev => ({ ...prev, rate: Number(e.detail.value ?? 0) })); setRateManuallyEdited(true); }}
                      className={styles.input}
                    />
                  </IonLabel>
                </IonLabel>

                <IonLabel className={styles.lineFormRow}>
                  <IonLabel className={styles.formGroup}>
                    <span className={styles.fieldLabel}>{tr('components.ServiceOrderModal.discount')}</span>
                    <IonInput
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0"
                      value={lineFormData.discount ?? ''}
                      onIonInput={e => {
                        const v = e.detail.value;
                        setLineFormData(prev => ({ ...prev, discount: v === '' || v === null || v === undefined ? undefined : Number(v) }));
                      }}
                      className={styles.input}
                    />
                  </IonLabel>
                  <IonLabel className={styles.formGroup}>
                    <span className={styles.fieldLabel}>{tr('components.ServiceOrderModal.discountType')}</span>
                    <IonRadioGroup
                      value={lineFormData.discountType ?? '%'}
                      onIonChange={e => setLineFormData(prev => ({ ...prev, discountType: e.detail.value as '%' | '$' }))}
                      className={styles.discountTypeRow}
                    >
                      <IonRadio value="%" className={styles.radioOption}>%</IonRadio>
                      <IonRadio value="$" className={styles.radioOption}>$</IonRadio>
                    </IonRadioGroup>
                  </IonLabel>
                </IonLabel>

                <IonLabel className={styles.formGroup}>
                  <span className={styles.fieldLabel}>{tr('components.ServiceOrderModal.tax')}</span>
                  <IonItem button detail={false} lines="none" ref={taxFieldRef} className={styles.selectField} disabled={loadingTaxes} onClick={() => setTaxPickerOpen(o => !o)}>
                    <IonLabel className={`${styles.selectFieldValue} ${lineFormData.tax_id === undefined ? styles.selectPlaceholder : ''}`}>
                      <span>
                        {(() => {
                          const t = zohoTaxes.find(tx => tx.tax_id === lineFormData.tax_id);
                          return t ? `${t.tax_name} (${t.tax_percentage}%)` : lineFormData.tax_id === '' ? 'Non-Taxable' : tr('components.ServiceOrderModal.selectATax');
                        })()}
                      </span>
                    </IonLabel>
                    <IonIcon icon={chevronDownOutline} slot="end" className={`${styles.selectCaret} ${taxPickerOpen ? styles.selectCaretOpen : ''}`} />
                  </IonItem>
                  <PortalPickerDropdown isOpen={taxPickerOpen} fieldRef={taxFieldRef} portalRef={pickerPortalRef} close={closeTaxPicker} align="left" matchWidth exactWidth className={styles.calendarDropdown}>
                    <IonList lines="none" className={styles.dropdownList}>
                      <IonItem button detail={false} lines="none" className={styles.dropdownItem} onClick={() => { setLineFormData(prev => ({ ...prev, tax_id: '', tax_name: '' })); setTaxPickerOpen(false); }}>
                        <IonLabel><h3>{tr('components.ServiceOrderModal.nonTaxable')}</h3></IonLabel>
                      </IonItem>
                      {zohoTaxes.map(t => (
                        <IonItem
                          key={t.tax_id}
                          button
                          detail={false}
                          lines="none"
                          className={`${styles.dropdownItem}${lineFormData.tax_id === t.tax_id ? ` ${styles.dropdownItemActive}` : ''}`}
                          onClick={() => { setLineFormData(prev => ({ ...prev, tax_id: t.tax_id, tax_name: t.tax_name })); setTaxPickerOpen(false); }}
                        >
                          <IonLabel><h3>{t.tax_name} [{t.tax_percentage}%]</h3></IonLabel>
                          {lineFormData.tax_id === t.tax_id && <IonIcon icon={checkmark} slot="end" />}
                        </IonItem>
                      ))}
                    </IonList>
                  </PortalPickerDropdown>
                </IonLabel>

                <IonLabel className={styles.lineFormActions}>
                  {/* IonButtons (not just a plain wrapper) — Ionic applies its own
                      --padding-top/--padding-bottom/min-height context CSS to slotted
                      ion-button children only inside a real <ion-buttons>, which is
                      the actual reason .confirmBtn (in BasePageWithHeader's IonButtons
                      slot) renders shorter than a plain-content IonButton with
                      identical padding/font-size CSS. */}
                  <IonButtons className={styles.lineFormButtonsGroup}>
                    <IonButton fill="outline" className={styles.lineFormCancelBtn} onClick={() => { setLineFormOpen(false); setEditingIndex(null); }}>{tr('components.ServiceOrderModal.cancel')}
                    </IonButton>
                    {editingIndex === null && (
                      <IonButton fill="outline" className={styles.lineFormSaveNewBtn} disabled={!canSaveLineForm} onClick={() => saveLineForm(true)}>{tr('components.ServiceOrderModal.saveNew')}
                      </IonButton>
                    )}
                    <IonButton fill="solid" className={styles.lineFormSaveBtn} disabled={!canSaveLineForm} onClick={() => saveLineForm(false)}>{tr('components.ServiceOrderModal.save')}
                    </IonButton>
                  </IonButtons>
                </IonLabel>
              </IonLabel>
            ) : (
              <IonButton fill="clear" className={styles.addItemBtn} onClick={openAddItem}>
                <IonIcon icon={addCircleOutline} />{tr('components.ServiceOrderModal.addLineItem')}
              </IonButton>
            )}
          </IonCardContent>
        </IonCard>

        {/* ── Billing Summary ────────────────────────────────────── */}
        {lineItems.length > 0 && (
          <IonCard className={styles.card}>
            <IonCardContent className={styles.cardBody}>
              <IonLabel className={styles.cardTitle}>{tr('components.ServiceOrderModal.billingSummary')}</IonLabel>

              <IonLabel className={styles.billingRow}>
                <span>{tr('components.ServiceOrderModal.subtotal')}{isInclusiveTax ? tr('components.ServiceOrderModal.taxIncl') : ''}</span>
                <span>${subTotalAmt.toFixed(2)}</span>
              </IonLabel>

              {taxBreakdownRows.map(r => (
                <IonLabel key={r.name} className={styles.billingRow}>
                  <span>{r.name}</span><span>${r.amount.toFixed(2)}</span>
                </IonLabel>
              ))}
              {taxBreakdownRows.length > 0 && (
                <IonLabel className={styles.billingRow}>
                  <span>{tr('components.ServiceOrderModal.totalTax')}</span><span style={{ fontWeight: 700 }}>${totalTaxAmt.toFixed(2)}</span>
                </IonLabel>
              )}

              <IonLabel className={styles.billingRow}>
                <span>{tr('components.ServiceOrderModal.adjustment')}</span>
                <IonInput
                  type="text"
                  inputmode="decimal"
                  style={{ maxWidth: 100, textAlign: 'right' }}
                  value={adjustment}
                  placeholder="0.00"
                  onIonInput={e => setAdjustment(String(e.detail.value ?? ''))}
                  onIonBlur={() => {
                    const n = parseFloat(adjustment);
                    setAdjustment(isNaN(n) ? '' : String(n));
                  }}
                />
              </IonLabel>

              <IonLabel className={styles.billingRow} style={{ borderTop: '1px solid var(--glass-border)', paddingTop: 10, marginTop: 4 }}>
                <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{tr('components.ServiceOrderModal.total')}</span>
                <span style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--ion-color-primary)' }}>${grandTotal.toFixed(2)}</span>
              </IonLabel>
            </IonCardContent>
          </IonCard>
        )}

        {/* ── Section 5: Customer Notes ──────────────────────────── */}
        <IonCard className={styles.card}>
          <IonCardContent className={styles.cardBody}>
            <IonLabel className={styles.cardTitle}>
              <IonIcon icon={chatbubbleOutline} />{tr('components.ServiceOrderModal.customerNotes')}
            </IonLabel>
            <IonTextarea
              rows={3}
              autoGrow
              placeholder={tr('components.ServiceOrderModal.notesVisibleToTheCustomer')}
              value={notes}
              onIonInput={e => setNotes(String(e.detail.value ?? ''))}
              className={styles.textarea}
            />
          </IonCardContent>
        </IonCard>

        {/* ── Section 6: Terms & Conditions ─────────────────────── */}
        <IonCard className={styles.card}>
          <IonCardContent className={styles.cardBody}>
            <IonLabel className={styles.cardTitle}>
              <IonIcon icon={documentTextOutline} />{tr('components.ServiceOrderModal.termsConditions')}
            </IonLabel>
            <IonTextarea
              rows={3}
              autoGrow
              placeholder={tr('components.ServiceOrderModal.paymentPolicyCancellationTerms')}
              value={terms}
              onIonInput={e => setTerms(String(e.detail.value ?? ''))}
              className={styles.textarea}
            />
          </IonCardContent>
        </IonCard>

        {/* ── Section 7: Attachments ─────────────────────────────── */}
        <IonCard className={styles.card}>
          <IonCardContent className={styles.cardBody}>
            <IonLabel className={styles.cardTitle}>
              <IonIcon icon={attachOutline} />{tr('components.ServiceOrderModal.attachments')}
            </IonLabel>
            {attachments.length > 0 && (
              <IonLabel className={styles.attachmentList}>
                {attachments.map((file, i) => (
                  <IonItem key={i} lines="none" className={styles.attachmentChip}>
                    <IonIcon icon={imageOutline} className={styles.attachmentChipIcon} />
                    <IonLabel className={styles.attachmentChipName}>{file.name}</IonLabel>
                    <IonIcon
                      icon={closeCircleOutline}
                      className={styles.attachmentChipRemove}
                      onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                    />
                  </IonItem>
                ))}
              </IonLabel>
            )}
            <label className={styles.uploadArea}>
              <input
                type="file"
                multiple
                accept="image/*,application/pdf"
                style={{ display: 'none' }}
                onChange={e => {
                  if (e.target.files) {
                    setAttachments(prev => [...prev, ...Array.from(e.target.files!)]);
                    e.target.value = '';
                  }
                }}
              />
              <IonIcon icon={imageOutline} className={styles.uploadIcon} />
              <span className={styles.uploadLabel}>{tr('components.ServiceOrderModal.uploadFile')}</span>
            </label>
          </IonCardContent>
        </IonCard>

        </IonGrid>
      </IonContent>

      {/* ── Pickers ───────────────────────────────────────────────── */}
      <GlassPicker
        isOpen={soNumberAlertOpen}
        onClose={() => setSoNumberAlertOpen(false)}
        title={tr('components.ServiceOrderModal.salesOrder')}
        options={[
          {
            label: tr('components.ServiceOrderModal.continueAutoGeneratingSalesOrderNumbers'),
            value: 'auto',
            content: (
              <div style={{ display: 'flex', gap: 10, marginBottom: 4 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: 'var(--ion-color-medium)', marginBottom: 4 }}>{tr('components.ServiceOrderModal.prefix')}</div>
                  <input
                    readOnly
                    value="SO-"
                    style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '8px 10px', fontSize: 14, color: 'var(--ion-color-medium)' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: 'var(--ion-color-medium)', marginBottom: 4 }}>{tr('components.ServiceOrderModal.nextNumber')}</div>
                  <input
                    value={nextSoNumber}
                    onChange={e => setNextSoNumber(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '8px 10px', fontSize: 14, color: 'var(--ion-text-color)' }}
                  />
                </div>
              </div>
            ),
          },
          { label: tr('components.ServiceOrderModal.iWillAddThemManuallyEach'), value: 'manual-each' },
          { label: tr('components.ServiceOrderModal.iWillAddThemManuallyOnly'), value: 'manual-once' },
        ]}
        value={soNumberMode}
        radioMode
        onSelect={v => {
          const mode = v as 'auto' | 'manual-each' | 'manual-once';
          setSoNumberMode(mode);
          if (mode === 'auto') {
            setSoNumber('');
          } else if (!soNumber || soNumber === 'SO-') {
            // Pre-fill with current auto number so user only needs to tweak it
            setSoNumber(nextSoNumber ? 'SO-' + nextSoNumber : 'SO-');
          }
        }}
        onSave={async () => {
          // If in auto mode and user edited the Next Number, reset Zoho's counter
          if (soNumberMode === 'auto' && nextSoNumber) {
            const newNum = `SO-${nextSoNumber}`;
            try {
              await resetSoNumberSequence(newNum);
            } catch (e) {
              alert(tr('components.ServiceOrderModal.failedToResetSoSequence', { v1: e instanceof Error ? e.message : String(e) }));
            }
          }
          setSoNumberAlertOpen(false);
        }}
      />

    </IonModal>

      {/* ── Ellipsis Menu — same glass-panel dropdown chrome as every other
          picker in this form, fixed-positioned under the ⋮ button (matches
          CreateInvoice.tsx's .moreMenuPopover) instead of a native IonPopover,
          which rendered as a plain unstyled default-chrome list.
          Portaled straight to document.body — NOT into .pickerPortalRoot
          (lives inside this component's own <IonModal>) and NOT left as a
          plain sibling either, because ServiceOrderModal is itself opened
          from inside BookingForm.tsx's <IonModal>, so a plain sibling here
          is still nested inside THAT modal's wrapper. ion-modal's wrapper
          carries a persistent transform (used for its swipe-to-close gesture
          tracking, present even at rest, not just mid-animation) — any
          transformed ancestor traps backdrop-filter inside its own
          compositing layer, which is why the blur never visibly rendered.
          document.body is the only place guaranteed to have no such
          ancestor, however many modals deep this component is opened from. */}
      {menuOpen && createPortal(
        <IonCard ref={moreMenuCardRef} className={`${styles.calendarDropdown} ${styles.moreMenuPopover}`}>
          <IonList lines="none" className={styles.dropdownList}>
            <IonItem button detail={false} lines="none" className={styles.dropdownItem} onClick={() => { setMenuOpen(false); handleConfirm('send'); }}>
              <IonLabel><h3>{tr('components.ServiceOrderModal.saveSend')}</h3></IonLabel>
            </IonItem>
          </IonList>
        </IonCard>,
        document.body,
      )}
    </>
  );
};

export default ServiceOrderModal;
